import React, { useContext, useEffect, useRef } from 'react';
import { ColumnsType } from 'antd/es/table';
import { TableProps, Pagination, theme } from 'components';
import styled from 'styled-components';
import AnsweringSearchContext from './AnsweringSearchContext';
import { LayoutContext } from '../../../contexts';
import { useTableResizer } from '../../../hooks';
import { StyledTable } from '../../components';

const PAGE_SIZE = 20;

const SearchTable: React.FC<{
  dataSource?: readonly any[];
  columns: ColumnsType;
  loading?: boolean;
  onChange?: TableProps['onChange'];
  dataCy?: string;
  totalItems: number;
}> = ({ dataSource, columns, loading, onChange, dataCy, totalItems }) => {
  const { transition } = useContext(LayoutContext);
  const { page, setPage } = useContext(AnsweringSearchContext);
  const tableWrapperRef = useRef<HTMLDivElement>();
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const { tableHeight, tableHeaderHeight } = useTableResizer({
    wrapperRef: tableWrapperRef,
    windowResizeDelay: 200,
  });

  const pageHandler = (pageNumber: number) => {
    setPage(pageNumber - 1);
  };

  return (
    <div ref={tableWrapperRef}>
      <StyledSearchTable
        $height={tableHeight}
        $headerHeight={tableHeaderHeight}
        showSorterTooltip={false}
        loading={Boolean(loading)}
        dataSource={dataSource}
        columns={columns}
        onChange={onChange}
        scroll={{
          x: '100%',
          y: `calc(100% - ${tableHeaderHeight}px)`,
        }}
        pagination={false}
        style={{
          transition: `all ${transition}s`,
        }}
        data-cy={dataCy}
      />
      <StyledPagination
        current={page + 1}
        defaultPageSize={PAGE_SIZE}
        onChange={pageHandler}
        // TODO: need to discuss with product to decide whether we should limit the page to 500 or not
        total={Math.min(totalItems, 10000) || 0}
        showSizeChanger={false}
        // add dataCy to each pagination element
        itemRender={(tablePage, type, element: any) => ({
          ...element,
          props: {
            ...element.props,
            'data-cy': `${tablePage}-${type}-${page}`,
          },
        })}
      />
    </div>
  );
};

export default SearchTable;

const StyledSearchTable = styled(StyledTable)`
  .ant-table-filter-trigger.active .anticon-filter {
    color: #1665fc;
  }
`;

const StyledPagination = styled(Pagination)`
  border-top: 1px solid ${theme.gray3};
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
`;
